import { SectionReveal, StaggerReveal, StaggerItem } from "~/components/company/SectionReveal"

const techGroups = [
  {
    label: "Frontend",
    items: [
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg", name: "React" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg", name: "JavaScript" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg", name: "TypeScript" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", name: "Tailwind" },
    ],
  },
  {
    label: "Backend",
    items: [
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rails/rails-original-wordmark.svg", name: "Rails" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ruby/ruby-original.svg", name: "Ruby" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg", name: "Next.js" },
    ],
  },
  {
    label: "Database",
    items: [
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg", name: "PostgreSQL" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg", name: "Redis" },
      { src: "https://www.vectorlogo.zone/logos/elastic/elastic-icon.svg", name: "Elasticsearch" },
    ],
  },
  {
    label: "Cloud & DevOps",
    items: [
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg", name: "AWS" },
      { src: "/render-logo.svg", name: "Render" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/heroku/heroku-original.svg", name: "Heroku" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg", name: "Git" },
      { src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg", name: "Docker" },
      { src: "https://www.vectorlogo.zone/logos/semaphoreci/semaphoreci-icon.svg", name: "Semaphore" },
    ],
  },
]

const aiTools = [
  { src: "https://cursor.com/assets/images/logo.svg", name: "Cursor", caption: "AI-first Code Editor" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/8/8a/Claude_AI_logo.svg", name: "Claude", caption: "AI Assistant" },
  { src: "https://v0.dev/assets/icon.svg", name: "v0", caption: "AI UI Generator" },
]

const integrations = [
  { src: "https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png", name: "HubSpot", caption: "CRM & Marketing" },
  { src: "/klaviyo-svg.svg", name: "Klaviyo", caption: "Email Marketing" },
]

function TechCard({ src, name, caption }: { src: string; name: string; caption?: string }) {
  return (
    <div className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <img src={src} alt={name} className="w-10 h-10 object-contain mb-2" />
      <span className="text-sm font-medium text-gray-700">{name}</span>
      {caption && <span className="text-xs text-gray-500 mt-1 text-center">{caption}</span>}
    </div>
  )
}

export function TechnologyStackSection() {
  return (
    <section id="technologies" className="w-full py-12 md:py-24 lg:py-32">
      <div className="container px-8 md:px-12 lg:px-16">
        <SectionReveal className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-gray-900">Technology Stack</h2>
          <p className="max-w-[900px] text-gray-500 md:text-xl">
            Proven expertise across the full development stack
          </p>
        </SectionReveal>

        <StaggerReveal className="grid gap-8 lg:grid-cols-2" staggerDelay={0.15}>
          {techGroups.map((group) => (
            <StaggerItem key={group.label}>
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-primary rounded"></div>
                <h3 className="text-xl font-bold text-gray-900">{group.label}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {group.items.map((item) => (
                  <TechCard key={item.name} src={item.src} name={item.name} />
                ))}
              </div>
            </div>
            </StaggerItem>
          ))}
        </StaggerReveal>

        <div className="mt-16">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-8">
            <h3 className="text-2xl font-bold tracking-tighter text-gray-900">AI-Powered Tools</h3>
            <p className="max-w-[700px] text-gray-500 md:text-lg">
              Leveraging cutting-edge AI for enhanced productivity and code quality
            </p>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-3 gap-8 max-w-2xl">
              {aiTools.map((tool) => (
                <TechCard key={tool.name} src={tool.src} name={tool.name} caption={tool.caption} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-8">
            <h3 className="text-2xl font-bold tracking-tighter text-gray-900">Third-Party Integrations</h3>
            <p className="max-w-[700px] text-gray-500 md:text-lg">
              Seamlessly connect your applications with popular marketing and CRM platforms
            </p>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-2 gap-8 max-w-md">
              {integrations.map((item) => (
                <TechCard key={item.name} src={item.src} name={item.name} caption={item.caption} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
